import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Database, Calendar, ShieldAlert, ChevronRight, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/hooks/useLanguage';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export interface LeakItem {
  domain: string;
  breach_date: string;
  data_leaked: string;
  data_leaked_pl: string;
  leak_count: number;
  description: string;
  description_pl: string;
  id?: string; // Optional ID for fallback
}

interface LeakCardProps {
  item: LeakItem;
  index: number;
  formatDate: (dateString: string) => string;
  formatNumber: (num: number) => string;
}

export function LeakCard({ item, index, formatDate, formatNumber }: LeakCardProps) {
  const { language, t } = useLanguage();

  const stripHtml = (html: string) => {
    if (!html) return '';
    return html.replace(/<[^>]*>?/gm, ' ');
  };

  const description = stripHtml(language === 'en' ? item.description : item.description_pl);
  const dataLeaked = language === 'en' ? item.data_leaked : item.data_leaked_pl;

  // Parse data types string into array for tags
  const dataTypes = dataLeaked.split(',').map(type => type.trim()).filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="h-full"
    >
      <Card className="group relative h-full flex flex-col overflow-hidden border-border/50 bg-card/40 backdrop-blur-sm hover:border-red-500/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(239,68,68,0.15)]">
        {/* Glitch effect overlay on hover */}
        <div className="absolute inset-0 bg-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none mix-blend-overlay" />

        <div className="p-6 flex flex-col h-full relative z-10">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-md bg-red-500/10 text-red-500 group-hover:bg-red-500/20 transition-colors">
                <ShieldAlert className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-bold tracking-tight text-foreground group-hover:text-red-500 transition-colors">
                {item.domain || t('leaks.no_domain') || 'Unknown Source'}
              </h3>
            </div>
            <Badge variant="outline" className="font-mono text-xs border-red-500/20 text-red-500/80">
              {formatDate(item.breach_date)}
            </Badge>
          </div>

          <div className="mb-4 flex-grow">
            <p className="text-sm text-muted-foreground line-clamp-3 mb-4 leading-relaxed">
              {description}
            </p>

            <div className="flex flex-wrap gap-2 mt-2">
              {dataTypes.slice(0, 3).map((type, i) => (
                <Badge key={i} variant="secondary" className="text-[10px] bg-secondary/50 text-secondary-foreground/80">
                  {type}
                </Badge>
              ))}
              {dataTypes.length > 3 && (
                <Badge variant="secondary" className="text-[10px] bg-secondary/50">
                  +{dataTypes.length - 3}
                </Badge>
              )}
            </div>
          </div>

          <div className="mt-auto pt-4 border-t border-border/30 flex items-center justify-between">
            <div className="flex items-center text-sm text-muted-foreground">
              <Database className="h-4 w-4 mr-2 text-primary/70" />
              <span className="font-mono text-foreground">{formatNumber(item.leak_count)}</span>
              <span className="ml-1 text-xs opacity-70">{t('leaks.records')}</span>
            </div>

            <Button variant="ghost" size="sm" className="group/btn hover:text-red-500 hover:bg-red-500/10" asChild>
              <Link to={`/leaks/${item.domain || `unknown-${index}`}`}>
                {t('leaks.details')}
                <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
